import React, { useEffect, useState } from "react";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import {
  Form,
  Button,
  Alert,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "../../contextProviders/userContext";
import api from "../../api";
import { getNavigation } from "../../components/navigationConfig";
import "./Signin.css";

const wholeNumber = new Intl.NumberFormat("en-ZA");
const hourNumber = new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 1 });

const SignIn = () => {
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });
  const [error, setError] = useState(null);
  const { initializeAuth } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isBtnHovered, setIsBtnHovered] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [programmeStats, setProgrammeStats] = useState(null);
  const [statsUnavailable, setStatsUnavailable] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadProgrammeStats = async () => {
      try {
        const response = await api.get("/public/stats", { skipAuthRefresh: true });
        if (mounted) {
          setProgrammeStats(response.data);
          setStatsUnavailable(false);
        }
      } catch (statsError) {
        console.error("Unable to load programme statistics:", statsError);
        if (mounted) setStatsUnavailable(true);
      }
    };

    loadProgrammeStats();
    const refreshTimer = window.setInterval(loadProgrammeStats, 60_000);

    return () => {
      mounted = false;
      window.clearInterval(refreshTimer);
    };
  }, []);

  const isVerificationError =
    error && error.toLowerCase().includes("verify your email");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResendMessage(null);
    setLoading(true);

    try {
      const response = await api.post("/auth/signin", formData, {
        skipAuthRefresh: true,
      });

      if (response.status === 200) {
        console.log("Sign in successful:", response.data);

        sessionStorage.removeItem("wcco_mobile_prompt_dismissed");
        initializeAuth(response.data.token, response.data.user, response.data.refreshToken);

        navigate(getNavigation(response.data.user.role).home);
      } else {
        setError(response.data.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Error during sign in:", err);
      setError(err.response?.data?.error || "Error during sign in");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage(null);
    try {
      const response = await api.post(
        "/auth/resend-verification",
        { identifier: formData.identifier },
        { skipAuthRefresh: true }
      );
      setResendMessage(response.data.message);
    } catch (err) {
      setResendMessage(
        err.response?.data?.error ||
          "Unable to resend verification email. Please try again later."
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-page-container">
      {/* ── Left Visual Panel (Desktop) ── */}
      <div 
        className="d-none d-lg-flex flex-column justify-content-between p-5 text-white"
        style={{
          width: "45%",
          background: "var(--wcco-blue)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Background glow elements */}
        <div style={{
          position: "absolute",
          top: "-10%",
          right: "-10%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background: "var(--wcco-blue)",
          pointerEvents: "none"
        }} />
        <div style={{
          position: "absolute",
          bottom: "-10%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: "var(--wcco-blue)",
          pointerEvents: "none"
        }} />

        {/* Logo Badge */}
        <div style={{
          background: "#FFFFFF",
          padding: "0.6rem 1.2rem",
          borderRadius: "var(--radius-md)",
          display: "inline-block",
          alignSelf: "flex-start",
          boxShadow: "none",
          zIndex: 2
        }}>
          <img
            src="newlogo1.jpg"
            alt="CSD Logo"
            style={{ maxHeight: "48px", width: "auto", objectFit: "contain" }}
          />
        </div>

        {/* Main Brand Content */}
        <div className="my-auto text-start" style={{ zIndex: 2 }}>
          <h1 style={{ fontSize: "2.6rem", fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.5px", marginBottom: "1.5rem" }}>
            Wits Citizenship &<br />Community Outreach
          </h1>
          <p style={{ fontSize: "1.05rem", color: "rgba(255, 255, 255, 0.85)", lineHeight: 1.6, maxWidth: "450px", marginBottom: "2rem" }}>
            Empowering students to drive positive change through active community engagement, volunteering, and structured outreach programs.
          </p>

          <div className="signin-live-stats" aria-live="polite" aria-busy={!programmeStats && !statsUnavailable}>
            <div className="signin-live-stats__header">
              <span className="signin-live-stats__dot" aria-hidden="true" />
              {statsUnavailable
                ? "Live data temporarily unavailable"
                : programmeStats
                  ? "Live programme data"
                  : "Loading live programme data"}
            </div>
            <div className="signin-live-stats__grid">
              <div className="signin-live-stat">
                <strong>{programmeStats ? hourNumber.format(programmeStats.approvedHours) : "—"}</strong>
                <span>Approved hours</span>
              </div>
              <div className="signin-live-stat">
                <strong>{programmeStats ? wholeNumber.format(programmeStats.activeProjects) : "—"}</strong>
                <span>Active projects</span>
              </div>
              <div className="signin-live-stat">
                <strong>{programmeStats ? wholeNumber.format(programmeStats.volunteers) : "—"}</strong>
                <span>Volunteers</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div style={{ fontSize: "0.8rem", color: "rgba(255, 255, 255, 0.4)", zIndex: 2 }}>
          © {new Date().getFullYear()} WCCO. All rights reserved.
        </div>
      </div>

      {/* ── Right Form Column ── */}
      <div className="auth-form-column">
        <div 
          className="auth-card"
          style={{
            maxWidth: "440px"
          }}
        >
          {/* Logo (Mobile display only) */}
          <div className="d-lg-none text-center mb-4">
            <img
              src="newlogo1.jpg"
              alt="CSD Logo"
              style={{ maxHeight: "60px", width: "auto", objectFit: "contain", marginBottom: "1rem" }}
            />
          </div>

          <div className="text-start mb-4">
            <h3 style={{ color: "var(--color-primary)", fontWeight: 600, letterSpacing: "-0.5px", marginBottom: "0.4rem" }}>
              Sign In
            </h3>
            <p style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem", margin: 0 }}>
              Access your volunteer and engagement portal.
            </p>
          </div>

          {error && (
            <Alert 
              variant="danger" 
              style={{ 
                fontSize: "0.85rem", 
                borderRadius: "var(--radius-md)", 
                border: "none",
                backgroundColor: "rgba(27, 58, 135, 0.08)",
                color: "var(--color-red)",
                fontWeight: 500,
                textAlign: "left"
              }}
            >
              {error}
              {isVerificationError && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    disabled={resendLoading || !formData.identifier}
                    onClick={handleResendVerification}
                    style={{
                      backgroundColor: "var(--color-primary)",
                      borderColor: "var(--color-primary)",
                      borderRadius: "var(--radius-sm)",
                      fontWeight: 500,
                      fontSize: "0.8rem",
                    }}
                  >
                    {resendLoading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-1" />
                        Sending...
                      </>
                    ) : (
                      "Resend Verification Email"
                    )}
                  </Button>
                </div>
              )}
            </Alert>
          )}

          {resendMessage && (
            <Alert
              variant="info"
              style={{
                fontSize: "0.85rem",
                borderRadius: "var(--radius-md)",
                border: "none",
                backgroundColor: "rgba(27, 58, 135, 0.08)",
                color: "var(--color-teal)",
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              {resendMessage}
            </Alert>
          )}

          <Form onSubmit={handleSubmit} className="text-start">
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                Student Number or Email Address
              </Form.Label>
              <InputGroup style={{ borderRadius: "var(--radius-md)", overflow: "hidden" }}>
                <InputGroup.Text style={{ backgroundColor: "var(--color-bg)", borderColor: "var(--color-border)" }}>
                  <FaUser style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="e.g. student_number or email"
                  name="identifier"
                  value={formData.identifier}
                  onChange={handleChange}
                  required
                  style={{
                    borderColor: "var(--color-border)",
                    padding: "0.65rem 0.85rem",
                    fontSize: "0.95rem"
                  }}
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--color-text)", marginBottom: "0.4rem" }}>
                  Password
                </Form.Label>
                <Button
                  type="button"
                  variant="link"
                  onClick={() => navigate("/forgot-password")}
                  className="p-0 mb-1"
                  style={{
                    color: "var(--color-teal)",
                    fontSize: "0.82rem",
                    fontWeight: 500,
                    textDecoration: "none"
                  }}
                >
                  Forgot password?
                </Button>
              </div>
              <InputGroup style={{ borderRadius: "var(--radius-md)", overflow: "hidden" }}>
                <InputGroup.Text style={{ backgroundColor: "var(--color-bg)", borderColor: "var(--color-border)" }}>
                  <FaLock style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }} />
                </InputGroup.Text>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  style={{
                    borderColor: "var(--color-border)",
                    padding: "0.65rem 0.85rem",
                    fontSize: "0.95rem",
                    borderRight: "none"
                  }}
                />
                <Button
                  variant="outline-secondary"
                  onClick={togglePasswordVisibility}
                  style={{
                    borderColor: "var(--color-border)",
                    backgroundColor: "transparent",
                    color: "var(--color-text-muted)",
                    padding: "0 0.9rem",
                    display: "flex",
                    alignItems: "center"
                  }}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <Button
              type="submit"
              disabled={loading}
              onMouseEnter={() => setIsBtnHovered(true)}
              onMouseLeave={() => setIsBtnHovered(false)}
              style={{
                backgroundColor: isBtnHovered ? "var(--color-primary-hover)" : "var(--color-primary)",
                borderColor: isBtnHovered ? "var(--color-primary-hover)" : "var(--color-primary)",
                borderRadius: "var(--radius-md)",
                padding: "0.75rem",
                fontWeight: 500,
                fontSize: "0.95rem",
                width: "100%",
                transition: "all var(--transition)",
                boxShadow: isBtnHovered ? "var(--shadow-md)" : "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem"
              }}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span>Signing In...</span>
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </Form>

          <div className="text-center mt-4" style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)" }}>
            Don't have an account?{" "}
            <Link to="/signup" style={{ color: "var(--color-teal)", fontWeight: 500, textDecoration: "none" }}>
              Sign Up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
